import React, { useEffect, useMemo, useState } from 'react';
import config from '@/config';
import PropTypes from 'prop-types';
import api from '@/shared/api';
import { CommentIcon } from '@/shared/components/Icons';
import PopoverStickOnHover from '@/shared/components/PopoverStickOnHover';
import HandleHttpError from '@/shared/services/HandleHttpError';
import classNames from 'classnames';
import intlShape from '@/shared/utils/intlShape';

const TaskRemarkIcon = ({
  taskId,
  taskLevel,
  hasRemark,
  scheduleHasRemark,
  onOpenDetail,
  changedCounter,
  intl,
}) => {
  const [remark, setRemark] = useState(null);
  const [scheduleRemark, setScheduleRemark] = useState(null);
  const [loading, setLoading] = useState(null);
  const popoverHoverFocus = useMemo(
    () => (
      <span id="popover-trigger-hover-focus">
        {loading && <i className="fa fa-spinner fa-spin" />}
        {!loading && (
          <div>
            {remark ? <span style={{ whiteSpace: 'pre-line' }}>{remark}</span> : null}
            {remark && scheduleRemark ? (
              <hr className="dark-line" style={{ marginTop: '10px', marginBottom: '10px' }} />
            ) : null}
            {scheduleRemark ? (
              <span style={{ whiteSpace: 'pre-line' }}>{scheduleRemark}</span>
            ) : null}
          </div>
        )}
      </span>
    ),
    [loading, remark, scheduleRemark],
  );
  useEffect(() => {
    //Clear remark when changedCounter changes
    setRemark(null);
  }, [changedCounter]);

  if (typeof hasRemark === 'undefined' && typeof scheduleHasRemark === 'undefined') return null;

  const retrieveRemark = () => {
    if (remark || scheduleRemark) return;
    setLoading(true);

    const taskRemarkPromise =
      hasRemark && !remark
        ? api.base.get(
            `${config.appUrls.tasks}/api/plannedTasks/Remark/${taskId}?taskLevel=${taskLevel}`,
          )
        : Promise.resolve({ data: remark });

    const scheduleRemarkPromise =
      scheduleHasRemark && !scheduleRemark
        ? api.base.get(`${config.appUrls.tasks}/api/taskschedules/Remark/${taskId}`)
        : Promise.resolve({ data: scheduleRemark });

    Promise.all([taskRemarkPromise, scheduleRemarkPromise])
      .then(([{ data: taskRemark }, { data: newScheduleRemark }]) => {
        setRemark(taskRemark);
        setScheduleRemark(newScheduleRemark);
        setLoading(false);
      })
      .catch(error => {
        HandleHttpError(intl)(error);
        setLoading(false);
      });
  };

  return hasRemark || scheduleHasRemark ? (
    <div className="task-status-icon visible">
      <span
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <PopoverStickOnHover
          trigger={['hover', 'focus']}
          placement="top"
          component={popoverHoverFocus}
          shouldUpdatePosition
          onMouseEnter={retrieveRemark}
          onClick={() => onOpenDetail(taskId)}
        >
          <span className="explanation-icon">
            <CommentIcon />
          </span>
        </PopoverStickOnHover>
      </span>
    </div>
  ) : (
    <div className="task-status-icon">
      <span className="empty-react-icon"></span>
    </div>
  );
};
TaskRemarkIcon.propTypes = {
  taskId: PropTypes.string.isRequired,
  taskLevel: PropTypes.number.isRequired,
  hasRemark: PropTypes.bool,
  scheduleHasRemark: PropTypes.bool,
  onOpenDetail: PropTypes.func,
  intl: intlShape,
};
TaskRemarkIcon.defaultProps = {
  hasRemark: undefined,
  scheduleHasRemark: undefined,
  onOpenDetail: undefined,
};

export default TaskRemarkIcon;
